import { ClassValue } from 'clsx';
import { isEmpty, isEqual } from 'lodash';
import { CSSProperties, useCallback } from 'react';
import { Icon } from '@shared/components/Icon';
import { Text } from '@shared/components/Text';
import { useToast } from '@shared/components/Toast';
import { View } from '@shared/components/View';
import { TimeTrackerButton, isTimerRunning, useTimer } from '@shared/components/timer';
import {
  TimeTrackerComponentData,
  TimeTrackerField,
} from '@shared/data/entity/fields/TimeTrackerField';
import { useFieldDraft } from '@shared/scenes/entity/components/fields/useFieldDraft';
import { useUpdateWorkflowEntity } from '@shared/scenes/entity/edit/hooks/useUpdateWorkflowEntity';
import { API } from '@shared/types/api';
import { useBoolean } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { CollectionViewFieldContainer } from './CollectionViewFieldContainer';
import { FieldVariant } from './FieldVariant';
import { canStartStopTimer } from './util/canStartStopTimer';
import { getTimerText } from './util/getTimerText';
import styles from './TimeTrackerCollectionViewField.module.css';

//#region Public Interface

/** How often a running timer's time updates. */
const TIMER_TICK_MS = 1000;
const NO_TIMER_ENTRIES: TimeTrackerComponentData = [];

interface Props {
  variant: FieldVariant;
  className?: ClassValue;
  component: API.TimeTrackerComponent;
  entityId: string;
  /** None for a timer that has never run, which still offers its Start button. */
  field: TimeTrackerField | null;
  canUpdateCard: boolean;
  /** Not inline: the stopwatch beside the time, as tables and subtables show it. */
  showIcon?: boolean;
}

/**
 * Default and compact: the start/stop button (not without `canUpdateCard`, nor on a read-only
 * timer), then the short time, beside the stopwatch where the host asks for it, dimmed while
 * stopped, and the Start button alone for a timer that never ran. A change shows at once as a
 * draft while it saves. Inline: the stopwatch and the time.
 */
export function TimeTrackerCollectionViewField({
  variant,
  component,
  entityId,
  field,
  canUpdateCard,
  className,
  showIcon = false,
}: Props) {
  const savedEntries = field?.timerEntries ?? NO_TIMER_ENTRIES;
  const [draft, commitDraft] = useFieldDraft<TimeTrackerComponentData, TimeTrackerComponentData>(
    savedEntries,
    isEqual,
  );
  const entries = draft ?? savedEntries;
  const isInline = variant === 'inline';
  const hasEntries = !isEmpty(entries);
  const canStartStop = canStartStopTimer(component, canUpdateCard);

  // Nothing to show: no time logged, and no Start button (inline never has one).
  if (!hasEntries && (isInline || !canStartStop)) {
    return null;
  }

  const timer: TimerProps = {
    component,
    entityId,
    entries,
    isRunning: isTimerRunning(entries),
    hasEntries,
    canStartStop,
    commitDraft,
  };

  return (
    <CollectionViewFieldContainer
      fieldType="TimeTrackerField"
      variant={variant}
      className={[styles.timeTrackerCollectionViewField, className]}
    >
      {isInline && <InlineTimeTracker {...timer} />}
      {!isInline && <CompactTimeTracker {...timer} showIcon={showIcon} />}
    </CollectionViewFieldContainer>
  );
}

interface TimerProps {
  component: API.TimeTrackerComponent;
  entityId: string;
  entries: TimeTrackerComponentData;
  isRunning: boolean;
  hasEntries: boolean;
  canStartStop: boolean;
  commitDraft: (draft: TimeTrackerComponentData, committed: TimeTrackerComponentData) => void;
}

function InlineTimeTracker({ entries, isRunning }: TimerProps) {
  return (
    <>
      <Icon name="time-stopwatch" gridSize={2.5} className={styles.inlineIcon} />
      <Text inactive={!isRunning}>
        <TimerDuration entries={entries} isRunning={isRunning} />
      </Text>
    </>
  );
}

function CompactTimeTracker({
  component,
  entityId,
  entries,
  isRunning,
  hasEntries,
  canStartStop,
  commitDraft,
  showIcon,
}: TimerProps & { showIcon: boolean }) {
  return (
    <>
      {canStartStop && (
        <View stopPropagation className={styles.control}>
          <FieldUpdatingTimeTrackerButton
            entityId={entityId}
            component={component}
            data={entries}
            isRunning={isRunning}
            commitDraft={commitDraft}
          />
        </View>
      )}
      {hasEntries && (
        <View row centerVertically className={styles.line}>
          {showIcon && <Icon name="time-stopwatch" gridSize={3.5} className={styles.icon} />}
          <Text weight="normal" numberOfLines={1} className={styles.time} inactive={!isRunning}>
            <TimerDuration entries={entries} isRunning={isRunning} />
          </Text>
        </View>
      )}
    </>
  );
}

/** A timer's tracked time, ticking each second while it runs: "20m 05s". */
function TimerDuration({
  entries,
  isRunning,
}: {
  entries: TimeTrackerComponentData;
  isRunning: boolean;
}) {
  const now = useTimer(TIMER_TICK_MS, isRunning);
  return getTimerText(entries, false, now);
}

//#endregion

//#region Private Interface

export function FieldUpdatingTimeTrackerButton({
  entityId,
  component,
  data,
  isRunning,
  commitDraft,
  style,
}: {
  entityId: string;
  component: API.TimeTrackerComponent;
  data: TimeTrackerComponentData;
  isRunning: boolean;
  commitDraft: (draft: TimeTrackerComponentData, committed: TimeTrackerComponentData) => void;
  style?: CSSProperties;
}) {
  const { updateWorkflowEntity } = useUpdateWorkflowEntity();
  const [loading, startLoading, stopLoading] = useBoolean(false);
  const toast = useToast();

  // The timer starts or stops on screen at once; if the save fails, it goes back to what was saved.
  const handleDataChanged = useCallback(
    async (newData: TimeTrackerComponentData) => {
      commitDraft(newData, newData);
      startLoading();
      try {
        await updateWorkflowEntity(entityId, {
          [component.id]: newData,
        });
      } catch (error) {
        console.error(error);
        commitDraft(data, data);
        toast({ style: 'warning', message: i18n('errors.somethingWentWrong') });
      } finally {
        stopLoading();
      }
    },
    [
      entityId,
      component.id,
      data,
      updateWorkflowEntity,
      commitDraft,
      startLoading,
      stopLoading,
      toast,
    ],
  );

  return (
    <TimeTrackerButton
      component={component}
      data={data}
      loading={loading}
      isRunning={isRunning}
      style={style}
      onDataChanged={handleDataChanged}
    />
  );
}

//#endregion
